import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import {
  getQcScoringRulesConfigApi,
  updateQcScoringRulesConfigApi,
} from '@/lib/reports-api';
import type { QcScoringRulesConfig } from '@/lib/types';
import {
  isQcScoringRulesOrderValid,
  qcScoringRuleIntervals,
} from '@/lib/qc-scoring-rules';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #324 — 管理员「系统设置 → AI文本分析 → AI评分规则」（AI文本纠错下方）。
 *
 * 真实 GET/PATCH /system/qc-scoring-rules-config（ADMIN+，按 orgId 隔离）。仅存三条下界：
 * 合格 / 良好 / 优秀；不合格自动为 < 合格下界。运行时即时生效——保存后新 AI 评分按新阈值
 * 写回 qcResult（PASS/FAIL）与报告等级文案。顺序非法（合格 > 良好 / 良好 > 优秀）由后端返回
 * 明确中文 400，前端也做预校验。
 */
const INPUT =
  'w-24 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

interface Draft {
  qualifiedMin: string;
  goodMin: string;
  excellentMin: string;
}

function toDraft(c: QcScoringRulesConfig): Draft {
  return {
    qualifiedMin: String(c.qualifiedMin),
    goodMin: String(c.goodMin),
    excellentMin: String(c.excellentMin),
  };
}

export function AiScoringRulesSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'qc-scoring-rules-config'],
    queryFn: getQcScoringRulesConfigApi,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data?.config) setDraft(toDraft(query.data.config));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: Partial<QcScoringRulesConfig>) => updateQcScoringRulesConfigApi(body),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(toDraft(data.config));
      await queryClient.invalidateQueries({ queryKey: ['system', 'qc-scoring-rules-config'] });
    },
    onError: (err) =>
      setSaveError(
        err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。',
      ),
  });

  if (query.isLoading) {
    return (
      <Card title="AI评分规则">
        <LoadingState label="正在加载 AI 评分规则…" />
      </Card>
    );
  }
  if (query.isError || !draft) {
    return (
      <Card title="AI评分规则">
        <ErrorState
          title="加载 AI 评分规则失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const scoreMin = query.data?.range?.scoreMin ?? 0;
  const scoreMax = query.data?.range?.scoreMax ?? 100;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setSaved(false);
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const parsed: QcScoringRulesConfig = {
    qualifiedMin: Number(draft.qualifiedMin),
    goodMin: Number(draft.goodMin),
    excellentMin: Number(draft.excellentMin),
  };
  const orderValid = isQcScoringRulesOrderValid(parsed);
  const intervals = orderValid ? qcScoringRuleIntervals(parsed) : null;

  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    mutation.mutate({
      qualifiedMin: parsed.qualifiedMin,
      goodMin: parsed.goodMin,
      excellentMin: parsed.excellentMin,
    });
  };

  return (
    <Card
      title="AI评分规则"
      description="通话质检 AI 综合分的判级阈值，仅设三条下界（不合格自动为低于合格下界）。保存后运行时即时生效——新 AI 评分按新阈值写入合规结论（PASS/FAIL）与报告等级文案。默认：合格 ≥60、良好 ≥70、优秀 ≥80。"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="合格下界（≥ 即合规）" hint={`默认 60`}>
          <input
            className={INPUT}
            type="number"
            min={scoreMin}
            max={scoreMax}
            value={draft.qualifiedMin}
            disabled={!canEdit}
            onChange={(e) => set('qualifiedMin', e.target.value)}
            data-testid="qc-scoring-qualified-min"
          />
        </Field>
        <Field label="良好下界" hint={`默认 70`}>
          <input
            className={INPUT}
            type="number"
            min={scoreMin}
            max={scoreMax}
            value={draft.goodMin}
            disabled={!canEdit}
            onChange={(e) => set('goodMin', e.target.value)}
            data-testid="qc-scoring-good-min"
          />
        </Field>
        <Field label="优秀下界" hint={`默认 80`}>
          <input
            className={INPUT}
            type="number"
            min={scoreMin}
            max={scoreMax}
            value={draft.excellentMin}
            disabled={!canEdit}
            onChange={(e) => set('excellentMin', e.target.value)}
            data-testid="qc-scoring-excellent-min"
          />
        </Field>
      </div>

      {intervals ? (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="qc-scoring-intervals">
          {intervals.map((it) => (
            <span
              key={it.grade}
              className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2.5 py-1 text-xs text-slate-600"
            >
              <span className="font-medium text-slate-700">{it.grade}</span>
              <span className="text-slate-400">{it.range}</span>
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-xs text-amber-600">
          三条下界需在 {scoreMin}-{scoreMax} 且满足 合格 ≤ 良好 ≤ 优秀。
        </p>
      )}

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button className={PRIMARY_BTN} onClick={onSave} disabled={mutation.isPending || !orderValid}>
            {mutation.isPending ? '保存中…' : '保存配置'}
          </button>
          {saved ? <span className="text-xs text-emerald-600">已保存，运行时即时生效。</span> : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改 AI 评分规则。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}

function Field({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-slate-600">
        {label}
        <span className="ml-1 text-xs text-slate-400">（{hint}）</span>
      </span>
      {children}
    </label>
  );
}
